import type { VoiceLivePublicEvent } from '../machine/types';
import type { VoiceLiveServerMessage } from '../types';
import type { UseConversationTranscriptResult } from './useConversationTranscript';
import type { UseTokenUsageResult } from './useTokenUsage';
import type { UseVoiceLiveAudioResult } from './useVoiceLiveAudio';
import type { UseVoiceLiveTelemetryResult } from './useVoiceLiveTelemetry';

export interface ServerMessageRouterDeps {
  audio: Pick<UseVoiceLiveAudioResult, 'playDelta' | 'stopPlayback'>;
  transcript: Pick<UseConversationTranscriptResult, 'appendTurn' | 'markLastAssistantInterrupted'>;
  tokenUsage: Pick<UseTokenUsageResult, 'record'>;
  telemetry: Pick<UseVoiceLiveTelemetryResult, 'setIceServerUrls' | 'setFieldOneSessionId'>;
  send: (event: VoiceLivePublicEvent) => void;
  /** Read fresh per message: whether the live socket was negotiated audio-only. */
  socketIsAudioOnly: boolean;
  onSessionCreated?: (sessionId: number) => void;
}

/**
 * Translates one backend WebSocket message into machine events and subsystem calls.
 * Deliberately free of socket/React state so it can be exercised directly.
 */
export const routeServerMessage = (
  message: VoiceLiveServerMessage,
  {
    audio,
    transcript,
    tokenUsage,
    telemetry,
    send,
    socketIsAudioOnly,
    onSessionCreated,
  }: ServerMessageRouterDeps,
): void => {
  switch (message.type) {
    case 'session.updated':
      send({ type: 'SESSION_UPDATED' });
      break;
    case 'fieldone_session.created':
      if (message.session_id) {
        telemetry.setFieldOneSessionId(message.session_id);
        send({ type: 'FIELDONE_SESSION_CREATED', sessionId: message.session_id });
        onSessionCreated?.(message.session_id);
      }
      break;
    case 'ice_servers':
      if (message.servers && message.servers.length > 0) {
        telemetry.setIceServerUrls(message.servers);
        send({ type: 'ICE_SERVERS', servers: message.servers });
      }
      break;
    case 'avatar_error':
      send({
        type: 'AVATAR_FAILED',
        reason: message.error?.message ? `avatar_error:${message.error.message}` : 'avatar_error',
      });
      break;
    case 'response.audio.delta':
      if (socketIsAudioOnly && message.delta) {
        audio.playDelta(message.delta);
      }
      break;
    case 'conversation.item.truncated':
      if (socketIsAudioOnly) {
        audio.stopPlayback();
      }
      transcript.markLastAssistantInterrupted();
      break;
    case 'response.done':
      if (message.token_usage) {
        tokenUsage.record(message.token_usage);
      }
      if (message.transcript) {
        transcript.appendTurn('assistant', message.transcript);
      }
      break;
    case 'conversation.item.created':
      if (message.transcript) {
        transcript.appendTurn('user', message.transcript);
      }
      break;
    default:
      break;
  }
};
